import { AlertTriangle, CalendarDays, History, Loader2, RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { paymentAPI } from '../../services/api';
import { responseData } from '../../services/apiResponse';
import CancelAttemptDialog from './CancelAttemptDialog';
import PaymentRequestCard from './PaymentRequestCard';
import {
  filterPaymentRequests,
  getCreatedFrom,
  groupLegacyTransactions,
  REQUEST_STATUSES,
  TIME_FILTERS,
} from './paymentRequestUtils';

function errorMessage(error, fallback) {
  return error?.response?.data?.detail || error?.response?.data?.error || error?.message || fallback;
}

export default function PaymentHistory({ contact } = {}) {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const contactId = searchParams.get('contact_id') || '';
  const defaultPeriod = contactId ? 'all' : '30d';
  const period = searchParams.get('period') || defaultPeriod;
  const status = searchParams.get('status') || 'all';
  const [requests, setRequests] = useState([]);
  const [summary, setSummary] = useState({ request_count: 0, active_count: 0 });
  const [expandedRequestId, setExpandedRequestId] = useState(searchParams.get('request'));
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [commandError, setCommandError] = useState('');
  const [cancelTarget, setCancelTarget] = useState(null);
  const [busyAttemptId, setBusyAttemptId] = useState('');
  const [nextCursor, setNextCursor] = useState(null);
  const requestVersion = useRef(0);
  const pollFailures = useRef(0);

  const loadRequests = useCallback(async ({ background = false, cursor = '', append = false } = {}) => {
    const version = ++requestVersion.current;
    if (background) setRefreshing(true);
    else setLoading(true);
    setLoadError('');
    const params = {};
    if (contactId) params.contact_id = contactId;
    const createdFrom = getCreatedFrom(period);
    if (createdFrom) params.created_from = createdFrom;
    if (status !== 'all') params.status = status;
    if (cursor) params.cursor = cursor;

    try {
      let nextRequests;
      let nextSummary;
      let loadedNextCursor = null;
      try {
        const response = await paymentAPI.getPaymentRequests(params);
        nextRequests = responseData(response).requests || [];
        nextSummary = responseData(response).summary;
        loadedNextCursor = responseData(response).next_cursor || null;
      } catch (error) {
        if (contactId || (error.response?.status !== 404 && error.response?.status !== 405)) throw error;
        const legacyResponse = await paymentAPI.getUserTransactions();
        nextRequests = filterPaymentRequests(
          groupLegacyTransactions(responseData(legacyResponse).transactions || []),
          period,
          status
        );
      }
      if (version !== requestVersion.current) return;
      setNextCursor(loadedNextCursor);
      const activeCount = nextRequests.filter((request) => request.status === 'pending' || request.status === 'processing').length;
      setRequests((current) => append
        ? [...current, ...nextRequests.filter((item) => !current.some((existing) => existing.request_id === item.request_id))]
        : nextRequests);
      setSummary(nextSummary || { request_count: nextRequests.length, active_count: activeCount });
      setExpandedRequestId((current) => {
        if (append && current) return current;
        if (current && nextRequests.some((request) => request.request_id === current)) return current;
        return null;
      });
      pollFailures.current = 0;
      return true;
    } catch (error) {
      if (version === requestVersion.current) {
        setLoadError(errorMessage(error, 'Payment requests could not be loaded. Please try again.'));
        pollFailures.current += 1;
      }
      return false;
    } finally {
      if (version === requestVersion.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [contactId, period, status]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const shouldPoll = useMemo(() => requests.some((request) => (
    request.status === 'pending' || request.status === 'processing' || request.payout_status === 'processing'
  )), [requests]);

  useEffect(() => {
    if (!shouldPoll) return undefined;
    let stopped = false;
    let timer;
    const baseDelay = requests.some((request) => request.status === 'processing') ? 15000
      : requests.some((request) => request.payout_status === 'processing') ? 30000 : 60000;
    const schedule = () => {
      const delay = Math.min(120000, baseDelay * (2 ** Math.min(pollFailures.current, 3)));
      timer = window.setTimeout(async () => {
        if (!stopped && document.visibilityState === 'visible') await loadRequests({ background: true });
        if (!stopped) schedule();
      }, delay);
    };
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      window.clearTimeout(timer);
      void loadRequests({ background: true }).finally(schedule);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    schedule();
    return () => {
      stopped = true;
      window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [loadRequests, requests, shouldPoll]);

  const setFilter = (key, value) => {
    const next = new URLSearchParams(searchParams);
    if ((key === 'status' && value === 'all') || (key === 'period' && value === defaultPeriod)) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const setExpandedRequest = (requestId) => {
    setExpandedRequestId(requestId);
    const next = new URLSearchParams(searchParams);
    if (requestId) next.set('request', requestId);
    else next.delete('request');
    setSearchParams(next, { replace: true });
  };

  const handleContinue = (request) => {
    navigate(`/review/${encodeURIComponent(request.request_id)}`);
  };

  const handleRequestToggle = (request) => {
    setExpandedRequest(expandedRequestId === request.request_id ? null : request.request_id);
  };

  const confirmCancel = async () => {
    if (!cancelTarget) return;
    setBusyAttemptId(cancelTarget.attempt.attempt_id);
    setCommandError('');
    try {
      await paymentAPI.cancelPaymentAttempt(cancelTarget.requestId, cancelTarget.attempt.attempt_id);
      setCancelTarget(null);
      await loadRequests({ background: true });
    } catch (error) {
      setCommandError(errorMessage(error, 'This payment attempt could not be cancelled.'));
    } finally {
      setBusyAttemptId('');
    }
  };

  return (
    <section aria-labelledby="payment-history-title">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 id="payment-history-title" className="text-lg font-semibold text-slate-900">Payment requests</h1>
          {contactId ? (
            <div className="mt-2 text-sm text-slate-600">
              <p>Transfers to {contact?.full_name || 'this receiver'}{contact?.phone ? ` · ${contact.phone}` : ''}</p>
              <div className="mt-2 flex flex-wrap gap-4 text-xs font-semibold text-cyan-800">
                <Link to={`/contacts/${encodeURIComponent(contactId)}`} className="hover:underline">Back to contact</Link>
                <button type="button" onClick={() => {
                  const next = new URLSearchParams(searchParams);
                  next.delete('contact_id');
                  setSearchParams(next, { replace: true });
                }} className="hover:underline">All payment requests</button>
              </div>
            </div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => loadRequests({ background: true })}
          disabled={refreshing}
          className="inline-flex h-8 items-center gap-1.5 self-start rounded px-2 text-xs font-semibold text-cyan-700 hover:bg-cyan-50 disabled:cursor-wait disabled:opacity-60 sm:self-auto"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="grid grid-cols-2 gap-2 sm:flex">
          <label className="relative min-w-0">
            <span className="sr-only">Request time range</span>
            <CalendarDays className="pointer-events-none absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-500" />
            <select value={period} onChange={(event) => setFilter('period', event.target.value)} className="h-9 w-full min-w-0 appearance-none rounded border border-slate-300 bg-white pl-8 pr-7 text-xs font-medium text-slate-700 outline-none focus:border-cyan-600 sm:w-auto sm:min-w-36">
              {TIME_FILTERS.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
          <label className="min-w-0">
            <span className="sr-only">Request status</span>
            <select value={status} onChange={(event) => setFilter('status', event.target.value)} className="h-9 w-full min-w-0 rounded border border-slate-300 bg-white px-3 text-xs font-medium text-slate-700 outline-none focus:border-cyan-600 sm:w-auto sm:min-w-36">
              {REQUEST_STATUSES.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}
            </select>
          </label>
        </div>
        {!loading ? <p className="hidden text-xs text-slate-500 sm:block">{summary.request_count} requests · {summary.active_count} active</p> : null}
      </div>

      {commandError && !cancelTarget ? (
        <div className="mt-4 flex items-start gap-2 rounded border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" role="status" aria-live="polite">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
          <span>{commandError}</span>
        </div>
      ) : null}

      {loading ? (
        <div className="flex min-h-64 flex-col items-center justify-center text-slate-500">
          <Loader2 className="h-6 w-6 animate-spin text-cyan-700" />
          <p className="mt-3 text-sm">Loading payment requests…</p>
        </div>
      ) : loadError ? (
        <div className="mt-5 rounded-md border border-rose-200 bg-rose-50 px-5 py-8 text-center">
          <AlertTriangle className="mx-auto h-7 w-7 text-rose-600" />
          <p className="mt-3 text-sm font-medium text-rose-800">{loadError}</p>
          <button type="button" onClick={() => loadRequests()} className="mt-4 rounded border border-rose-300 bg-white px-3 py-2 text-xs font-semibold text-rose-700 hover:bg-rose-100">Try again</button>
        </div>
      ) : requests.length === 0 ? (
        <div className="mt-5 rounded-md border border-dashed border-slate-300 px-5 py-12 text-center">
          <History className="mx-auto h-8 w-8 text-slate-300" />
          <p className="mt-3 text-sm font-semibold text-slate-700">{contactId ? 'No transfers found for this receiver' : 'No payment requests found'}</p>
          <p className="mt-1 text-xs text-slate-500">Try a different time range or status.</p>
        </div>
      ) : (
        <div className="mt-3 space-y-2 [content-visibility:auto]">
          {requests.map((request) => (
            <PaymentRequestCard
              key={request.request_id}
              request={request}
              expanded={expandedRequestId === request.request_id}
              busyAttemptId={busyAttemptId}
              onToggle={() => handleRequestToggle(request)}
              onContinue={handleContinue}
              onCancel={(attempt) => {
                setCommandError('');
                setCancelTarget({ requestId: request.request_id, attempt });
              }}
              onOpenDetails={() => navigate(`/payment-requests/${encodeURIComponent(request.request_id)}`)}
            />
          ))}
          {nextCursor ? (
            <div className="flex justify-center pt-3">
              <button
                type="button"
                onClick={() => loadRequests({ background: true, cursor: nextCursor, append: true })}
                disabled={refreshing}
                className="inline-flex h-9 items-center gap-2 rounded border border-slate-300 bg-white px-4 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-wait disabled:opacity-60"
              >
                {refreshing ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : null}
                Load more
              </button>
            </div>
          ) : null}
        </div>
      )}

      <CancelAttemptDialog
        attempt={cancelTarget?.attempt}
        loading={Boolean(busyAttemptId)}
        error={cancelTarget ? commandError : ''}
        onCancel={confirmCancel}
        onClose={() => {
          if (!busyAttemptId) {
            setCancelTarget(null);
            setCommandError('');
          }
        }}
      />
    </section>
  );
}
